import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle
} from "@material-ui/core";
import { ApiStateType } from "customHooks/useApi";
import FormModal from "customHooks/useCompleoReactHookForm/components/FormModal";
import Loading from "customHooks/useCompleoReactHookForm/helpers/Loading";
import { IValidateOnEntryDefinition } from "customHooks/useCompleoReactHookForm/Inputs/TagsCreatable";
import { validateEmail } from "functions/util";
import useGetMetadata from "customHooks/useGetMetadata";
import { useTranslation } from "react-i18next";
import useValuesFromSource from "customHooks/useValuesFromSource";
import _ from "lodash";
import useCustomLists from "./useCustomLists";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import useCompleoReactHookForm, {
    useCompleoReactHookFormGetMainData
} from "customHooks/useCompleoReactHookForm";
import { ModalDataType } from "./helperPipeline";
import { useGenericStageActionsModalHandleFieldsToHide } from "./useGenericStageActionsModalHandleFieldsToHide";

export interface IGenericStageActionsModal {
    open: boolean;
    onClose: () => void;
    fieldName: string;
    module: string;
    setValue: UseFormSetValue<FieldValues>;
    initialValuesDB: Compleo.IObject;
    jobId: string;
    mainPipelineData?: Compleo.IObject[];
    modalData?: ModalDataType;
}

const GenericStageActionsModal = (props: IGenericStageActionsModal) => {
    const {
        open,
        onClose,
        fieldName,
        module,
        setValue,
        initialValuesDB = {},
        jobId,
        mainPipelineData,
        modalData
    } = props;

    const [isUpdating, setIsUpdating] = React.useState(false);
    const [metadata] = useGetMetadata(module);

    const [t, i18n, readyTranslation] = useTranslation(module, {
        useSuspense: false
    });

    const [listDefinition, listsReady] = useCustomLists({
        t,
        readyTranslation,
        jobId,
        mainPipelineData,
        modalData
    });

    const [initialValues] = useValuesFromSource(metadata, false, {
        _ignoreUploadEmailFields: true,
        _ignoreUploadFileFields: true,
        ...initialValuesDB
    });

    const tagsAddProps: IValidateOnEntryDefinition = {
        errorMessage: t("actionApplicantRequestEvaluation.invalidEmail"),
        validateFunction: validateEmail
    };
    const addProps: Compleo.useCompleoForm.AdditionalPropertiesField[] = [
        {
            fieldName: "actionApplicantRequestEvaluation.externalEvaluators",
            props: {
                validateOnEntryDefinition: tagsAddProps
            }
        }
    ];

    // Issue #249 — When reopening the "Send Assessment" action config, the
    // assessment field could appear empty if the saved value isn't found in
    // the asynchronously loaded list. Pass notFoundMessage so ComboAutoComplete
    // shows a warning instead of silently resetting the field.
    if (fieldName === "actionApplicantSendAssessment") {
        addProps.push({
            fieldName: "assessment",
            props: {
                notFoundMessage: t("assessmentNotFoundWarning")
            }
        });
    }

    const ready =
        readyTranslation && metadata.status === "success" && listsReady;

    const reactHookFormMethods = useCompleoReactHookFormGetMainData({
        t,
        ready: ready,
        i18nV: i18n,
        metadadosRetorno: metadata,
        valuesFromSource: initialValues
    });
    const { watch } = reactHookFormMethods.reactHookFormMethods;
    const fieldsToHide = useGenericStageActionsModalHandleFieldsToHide({
        watch,
        mainPipelineData: mainPipelineData
    });

    const handleSubmit = async (values: any) => {
        const retorno: Compleo.IObject = {};
        retorno.status = 200;
        setIsUpdating(true);
        retorno.status = 200;
        try {
            const newValues = _.cloneDeep(values);
            delete newValues._ignoreUploadEmailFields;
            delete newValues._ignoreUploadFileFields;
            setValue(fieldName, [newValues]);
        } catch (ex) {
            setIsUpdating(false);
            return ex.response;
        }
        setIsUpdating(false);

        onClose();
        return retorno;
    };

    const postReturn: ApiStateType = {
        status: "success",
        response: {},
        exception: null
    };

    const [formCompleo, finished, handleRHFSubmit] = useCompleoReactHookForm({
        t: t,
        ready: ready,
        i18nV: i18n,
        postMethod: handleSubmit,
        postReturn: postReturn,
        FormType: FormModal,
        metadadosRetorno: metadata,
        valuesFromSource: initialValues,
        formGroupPaperElevation: 0,
        customLists: listDefinition,
        additionalFieldProperties: addProps,
        reactHookFormMethods: reactHookFormMethods,
        fieldsToHide: fieldsToHide
    });

    const handleSave = async () => {
        handleRHFSubmit();
    };

    let FormReturn: JSX.Element = <Loading />;
    if (finished && ready) {
        FormReturn = formCompleo;
    }
    if (ready) {
        return (
            <Dialog
                fullWidth
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth="lg"
                aria-labelledby={t("a_ModalTitle")}
                open={open}
            >
                <DialogTitle>{t("a_ModalTitle")}</DialogTitle>
                <DialogContent dividers>{FormReturn}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => onClose()}
                        color="secondary"
                        disabled={isUpdating}
                    >
                        {t("COMPLEOGENERAL_CANCEL")}
                    </Button>
                    <Button
                        onClick={() => handleSave()}
                        variant="contained"
                        color="primary"
                        disabled={isUpdating}
                    >
                        {t("COMPLEOGENERAL_SAVE")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    } else {
        return (
            <Dialog
                fullWidth
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth="md"
                aria-labelledby={t("a_FeatureMainDescription")}
                open={open}
            >
                <DialogContent dividers>
                    <Loading />
                </DialogContent>
            </Dialog>
        );
    }
};

export default GenericStageActionsModal;
